import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Button,
  Table,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Card,
  Select,
  Option,
  Textarea,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Chip
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { formatDisplayDate } from '../util/formatDate';
import StockDetailLayout from './StockDetailLayout';
import ResponsiveDetailTable from './ResponsiveDetailTable';
import DetailFieldCard from './DetailFieldCard';
import ExpiryDateShortcuts from './ExpiryDateShortcuts';
import { YEAR_END_SHORTCUTS } from './formCommon';

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_LABELS = {
  purchased: 'Purchased',
  installed: 'Installed',
  in_service: 'In Service',
  broken: 'Broken',
  scraped: 'Scraped'
};

const AMC_STATUS = { active: 'Active', expired: 'Expired', cancelled: 'Cancelled', pending: 'Pending' };
const SVC_STATUS = { in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

const EquipmentPopup = ({ equipmentId, onClose, variant = 'modal' }) => {
  const isPage = variant === 'page';
  const [equipment, setEquipment] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [amcContracts, setAmcContracts] = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [vendors, setVendors] = useState([]);

  const [statusModal, setStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: 'installed', changeDate: today(), remarks: '' });
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [amcModal, setAmcModal] = useState(null);
  const [amcForm, setAmcForm] = useState({
    provider_name: '', provider_contact: '', start_date: today(), end_date: '',
    contract_cost: '', status: 'active', notes: ''
  });
  const [amcHistory, setAmcHistory] = useState([]);
  const [amcHistoryModal, setAmcHistoryModal] = useState(null);
  const [amcEventForm, setAmcEventForm] = useState({ event_type: 'visit', event_date: today(), details: '' });

  const [svcModal, setSvcModal] = useState(null);
  const [svcForm, setSvcForm] = useState({
    problem_reported: '', service_provider: '', technician_name: '', technician_contact: '',
    reported_date: today(), out_date: '', in_date: '', service_charge: '', parts_replaced: '',
    status: 'in_progress', remarks: ''
  });
  const [svcHistory, setSvcHistory] = useState([]);
  const [svcHistoryModal, setSvcHistoryModal] = useState(null);
  const [svcEventForm, setSvcEventForm] = useState({ event_type: 'note', event_date: today(), details: '' });

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [eqRes, histRes, amcRes, svcRes] = await Promise.all([
        axiosInstance.get(`/equipment/${equipmentId}`),
        axiosInstance.get(`/equipment/${equipmentId}/status-history`),
        axiosInstance.get(`/equipment/${equipmentId}/amc`),
        axiosInstance.get(`/equipment/${equipmentId}/service`)
      ]);
      setEquipment(eqRes.data.equipment);
      setStatusHistory(histRes.data.history || []);
      setAmcContracts(amcRes.data.contracts || []);
      setServiceRecords(svcRes.data.serviceRecords || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const fetchVendors = async () => {
    if (vendors.length > 0) return;
    const res = await axiosInstance.get('/vendors');
    setVendors(res.data.vendors || []);
  };

  const coverageBanner = () => {
    const c = equipment?.coverage;
    if (!c) return null;
    if (c.under_warranty) {
      return (
        <Alert color="primary" variant="soft" sx={{ mb: 2 }}>
          Under warranty until {formatDisplayDate(equipment.warranty_until)}
        </Alert>
      );
    }
    if (c.amc_active) {
      return <Alert color="success" variant="soft" sx={{ mb: 2 }}>Active AMC contract — use AMC tab for contract-covered maintenance</Alert>;
    }
    return <Alert color="warning" variant="soft" sx={{ mb: 2 }}>No warranty or AMC — use Service tab for paid repairs</Alert>;
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    setStatusSubmitting(true);
    try {
      await axiosInstance.put(`/equipment/${equipmentId}/status`, statusForm);
      setStatusModal(false);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusSubmitting(false);
    }
  };

  const openEdit = async () => {
    await fetchVendors();
    setEditForm({
      equipment_name: equipment.equipment_name,
      purchase_date: equipment.purchase_date?.slice(0, 10) || '',
      purchase_cost: equipment.purchase_cost != null ? String(equipment.purchase_cost) : '',
      warranty_until: equipment.warranty_until?.slice(0, 10) || '',
      serial_no: equipment.serial_no || '',
      brand_make: equipment.brand_make || '',
      vendor_id: equipment.vendor_id != null ? String(equipment.vendor_id) : '',
      location: equipment.location || '',
      notes: equipment.notes || ''
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      await axiosInstance.put(`/equipment/${equipmentId}`, {
        ...editForm,
        purchase_cost: editForm.purchase_cost !== '' ? editForm.purchase_cost : null,
        vendor_id: editForm.vendor_id || null
      });
      setEditOpen(false);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update equipment');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openAmcModal = (contract) => {
    if (contract) {
      setAmcForm({
        provider_name: contract.provider_name,
        provider_contact: contract.provider_contact || '',
        start_date: contract.start_date?.slice(0, 10) || '',
        end_date: contract.end_date?.slice(0, 10) || '',
        contract_cost: contract.contract_cost != null ? String(contract.contract_cost) : '',
        status: contract.status,
        notes: contract.notes || ''
      });
      setAmcModal({ mode: 'edit', id: contract.id });
    } else {
      setAmcForm({
        provider_name: '', provider_contact: '', start_date: today(), end_date: '',
        contract_cost: '', status: 'active', notes: ''
      });
      setAmcModal({ mode: 'create' });
    }
  };

  const handleAmcSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...amcForm,
        contract_cost: amcForm.contract_cost !== '' ? amcForm.contract_cost : null
      };
      if (amcModal.mode === 'create') {
        await axiosInstance.post(`/equipment/${equipmentId}/amc`, payload);
      } else {
        await axiosInstance.put(`/equipment/${equipmentId}/amc/${amcModal.id}`, payload);
      }
      setAmcModal(null);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save AMC contract');
    }
  };

  const loadAmcHistory = async (contractId) => {
    const res = await axiosInstance.get(`/equipment/${equipmentId}/amc/${contractId}/history`);
    setAmcHistory(res.data.history || []);
    setAmcHistoryModal(contractId);
  };

  const handleAmcEventSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post(`/equipment/${equipmentId}/amc/${amcHistoryModal}/history`, amcEventForm);
      await loadAmcHistory(amcHistoryModal);
      setAmcEventForm({ event_type: 'visit', event_date: today(), details: '' });
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add AMC event');
    }
  };

  const openSvcModal = (record) => {
    setWarning('');
    if (record) {
      setSvcForm({
        problem_reported: record.problem_reported,
        service_provider: record.service_provider || '',
        technician_name: record.technician_name || '',
        technician_contact: record.technician_contact || '',
        reported_date: record.reported_date?.slice(0, 10) || today(),
        out_date: record.out_date?.slice(0, 10) || '',
        in_date: record.in_date?.slice(0, 10) || '',
        service_charge: record.service_charge != null ? String(record.service_charge) : '',
        parts_replaced: record.parts_replaced || '',
        status: record.status,
        remarks: record.remarks || ''
      });
      setSvcModal({ mode: 'edit', id: record.id });
    } else {
      setSvcForm({
        problem_reported: '', service_provider: '', technician_name: '', technician_contact: '',
        reported_date: today(), out_date: '', in_date: '', service_charge: '', parts_replaced: '',
        status: 'in_progress', remarks: ''
      });
      setSvcModal({ mode: 'create' });
    }
  };

  const handleSvcSubmit = async (e) => {
    e.preventDefault();
    setWarning('');
    try {
      const payload = {
        ...svcForm,
        service_charge: svcForm.service_charge !== '' ? svcForm.service_charge : null
      };
      if (svcModal.mode === 'create') {
        const res = await axiosInstance.post(`/equipment/${equipmentId}/service`, payload);
        if (res.data.warnings?.length) {
          setWarning(res.data.warnings.join('. '));
        }
      } else {
        await axiosInstance.put(`/equipment/${equipmentId}/service/${svcModal.id}`, payload);
      }
      setSvcModal(null);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save service record');
    }
  };

  const loadSvcHistory = async (serviceId) => {
    const res = await axiosInstance.get(`/equipment/${equipmentId}/service/${serviceId}/history`);
    setSvcHistory(res.data.history || []);
    setSvcHistoryModal(serviceId);
  };

  const handleSvcEventSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post(`/equipment/${equipmentId}/service/${svcHistoryModal}/history`, svcEventForm);
      await loadSvcHistory(svcHistoryModal);
      setSvcEventForm({ event_type: 'note', event_date: today(), details: '' });
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add service event');
    }
  };

  const statusActions = equipment?.status !== 'scraped' ? (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      {equipment?.status === 'purchased' && (
        <Button size="sm" onClick={() => { setStatusForm({ status: 'installed', changeDate: today(), remarks: 'Installed' }); setStatusModal(true); }}>Mark Installed</Button>
      )}
      {['installed', 'broken'].includes(equipment?.status) && (
        <Button size="sm" color="danger" variant="outlined" onClick={() => { setStatusForm({ status: 'broken', changeDate: today(), remarks: '' }); setStatusModal(true); }}>Mark Broken</Button>
      )}
      {equipment?.status !== 'scraped' && (
        <Button size="sm" color="neutral" variant="outlined" onClick={() => { setStatusForm({ status: 'scraped', changeDate: today(), remarks: 'Scrapped' }); setStatusModal(true); }}>Scrap</Button>
      )}
      <Button size="sm" variant="outlined" onClick={() => { setStatusForm({ status: equipment.status, changeDate: today(), remarks: '' }); setStatusModal(true); }}>Change Status</Button>
      <Button size="sm" variant="plain" onClick={openEdit}>Edit Details</Button>
    </Box>
  ) : (
    <Button size="sm" variant="plain" onClick={openEdit} sx={{ mb: 2 }}>Edit Details</Button>
  );

  const detailBody = loading ? (
    <Typography>Loading...</Typography>
  ) : equipment ? (
          <>
            {!isPage && (
              <>
                <Typography level="h3" sx={{ mb: 1 }}>{equipment.equipment_name}</Typography>
                <Chip size="sm" sx={{ mb: 2 }}>{STATUS_LABELS[equipment.status]}</Chip>
              </>
            )}
            {isPage && (
              <Chip size="sm" sx={{ mb: 2 }}>{STATUS_LABELS[equipment.status]}</Chip>
            )}

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {warning && <Alert color="warning" sx={{ mb: 2 }}>{warning}</Alert>}
            {coverageBanner()}
            {statusActions}

            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '--Tabs-gap': '0.5rem' }}>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>AMC</Tab>
                <Tab>Service</Tab>
              </TabList>

              <TabPanel value={0}>
                <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography><strong>Serial:</strong> {equipment.serial_no || '—'}</Typography>
                  <Typography><strong>Brand/Make:</strong> {equipment.brand_make || '—'}</Typography>
                  <Typography><strong>Purchase:</strong> {formatDisplayDate(equipment.purchase_date)} {equipment.purchase_cost != null ? `(₹${equipment.purchase_cost})` : ''}</Typography>
                  <Typography><strong>Warranty until:</strong> {formatDisplayDate(equipment.warranty_until)}</Typography>
                  <Typography><strong>Vendor:</strong> {equipment.vendor_name || '—'}</Typography>
                  <Typography><strong>Location:</strong> {equipment.location || '—'}</Typography>
                  {equipment.notes && <Typography><strong>Notes:</strong> {equipment.notes}</Typography>}
                </Card>
                <Typography level="title-md" sx={{ mb: 1 }}>Status History</Typography>
                <ResponsiveDetailTable
                  mobile={isPage ? statusHistory.map((h) => (
                    <DetailFieldCard
                      key={h.id}
                      title={formatDisplayDate(h.change_date)}
                      fields={[
                        { label: 'From', value: h.previous_status ? STATUS_LABELS[h.previous_status] : '—' },
                        { label: 'To', value: STATUS_LABELS[h.new_status] },
                        { label: 'Remarks', value: h.remarks || '—' },
                        { label: 'By', value: h.created_by_name || '—' }
                      ]}
                    />
                  )) : null}
                >
                <Table size="sm" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                  <thead><tr><th>Date</th><th>From</th><th>To</th><th>Remarks</th><th>By</th></tr></thead>
                  <tbody>
                    {statusHistory.map((h) => (
                      <tr key={h.id}>
                        <td>{formatDisplayDate(h.change_date)}</td>
                        <td>{h.previous_status ? STATUS_LABELS[h.previous_status] : '—'}</td>
                        <td>{STATUS_LABELS[h.new_status]}</td>
                        <td>{h.remarks || '—'}</td>
                        <td>{h.created_by_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </ResponsiveDetailTable>
              </TabPanel>

              <TabPanel value={1}>
                <Button sx={{ mb: 2 }} onClick={() => openAmcModal(null)}>Add AMC Contract</Button>
                <ResponsiveDetailTable
                  mobile={isPage ? (
                    amcContracts.length === 0 ? (
                      <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No AMC contracts</Typography>
                    ) : amcContracts.map((c) => (
                      <DetailFieldCard
                        key={c.id}
                        title={c.provider_name}
                        subtitle={c.provider_contact || undefined}
                        fields={[
                          { label: 'Period', value: `${formatDisplayDate(c.start_date)} – ${formatDisplayDate(c.end_date)}` },
                          { label: 'Cost', value: c.contract_cost != null ? `₹${c.contract_cost}` : '—' },
                          { label: 'Status', value: AMC_STATUS[c.status] }
                        ]}
                        actions={(
                          <>
                            <Button size="sm" variant="outlined" onClick={() => openAmcModal(c)}>Edit</Button>
                            <Button size="sm" onClick={() => loadAmcHistory(c.id)}>History</Button>
                          </>
                        )}
                      />
                    ))
                  ) : null}
                >
                <Table size="sm" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                  <thead><tr><th>Provider</th><th>Period</th><th>Cost</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {amcContracts.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center' }}>No AMC contracts</td></tr>
                    ) : amcContracts.map((c) => (
                      <tr key={c.id}>
                        <td>{c.provider_name}<br /><Typography level="body-xs">{c.provider_contact}</Typography></td>
                        <td>{formatDisplayDate(c.start_date)} – {formatDisplayDate(c.end_date)}</td>
                        <td>{c.contract_cost != null ? `₹${c.contract_cost}` : '—'}</td>
                        <td><Chip size="sm">{AMC_STATUS[c.status]}</Chip></td>
                        <td>
                          <Button size="sm" variant="outlined" onClick={() => openAmcModal(c)}>Edit</Button>
                          <Button size="sm" sx={{ ml: 0.5 }} onClick={() => loadAmcHistory(c.id)}>History</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </ResponsiveDetailTable>
              </TabPanel>

              <TabPanel value={2}>
                {equipment?.coverage?.under_warranty || equipment?.coverage?.amc_active ? (
                  <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    Equipment has active warranty or AMC. Service records are for out-of-warranty / no-AMC repairs.
                  </Alert>
                ) : null}
                {equipment?.status !== 'scraped' && (
                  <Button sx={{ mb: 2 }} onClick={() => openSvcModal(null)}>Add Service Record</Button>
                )}
                <ResponsiveDetailTable
                  mobile={isPage ? (
                    serviceRecords.length === 0 ? (
                      <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No service records</Typography>
                    ) : serviceRecords.map((s) => (
                      <DetailFieldCard
                        key={s.id}
                        title={s.problem_reported}
                        fields={[
                          { label: 'Provider', value: s.service_provider || '—' },
                          { label: 'Technician', value: s.technician_name || '—' },
                          {
                            label: 'Dates',
                            value: [
                              s.out_date ? `Out: ${formatDisplayDate(s.out_date)}` : '',
                              s.in_date ? `In: ${formatDisplayDate(s.in_date)}` : ''
                            ].filter(Boolean).join(' ') || '—'
                          },
                          { label: 'Status', value: SVC_STATUS[s.status] }
                        ]}
                        actions={(
                          <>
                            <Button size="sm" variant="outlined" onClick={() => openSvcModal(s)}>Edit</Button>
                            <Button size="sm" onClick={() => loadSvcHistory(s.id)}>History</Button>
                          </>
                        )}
                      />
                    ))
                  ) : null}
                >
                <Table size="sm" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                  <thead><tr><th>Problem</th><th>Provider</th><th>Technician</th><th>Dates</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {serviceRecords.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center' }}>No service records</td></tr>
                    ) : serviceRecords.map((s) => (
                      <tr key={s.id}>
                        <td>{s.problem_reported}</td>
                        <td>{s.service_provider || '—'}</td>
                        <td>{s.technician_name || '—'}</td>
                        <td>
                          {s.out_date ? `Out: ${formatDisplayDate(s.out_date)}` : ''}
                          {s.in_date ? ` In: ${formatDisplayDate(s.in_date)}` : ''}
                        </td>
                        <td><Chip size="sm">{SVC_STATUS[s.status]}</Chip></td>
                        <td>
                          <Button size="sm" variant="outlined" onClick={() => openSvcModal(s)}>Edit</Button>
                          <Button size="sm" sx={{ ml: 0.5 }} onClick={() => loadSvcHistory(s.id)}>History</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </ResponsiveDetailTable>
              </TabPanel>
            </Tabs>
          </>
        ) : null;

  return (
    <>
      {isPage ? (
        <StockDetailLayout title={equipment?.equipment_name || 'Equipment'} onBack={onClose}>
          {detailBody}
        </StockDetailLayout>
      ) : (
        <Modal open onClose={onClose}>
          <ModalDialog sx={{ width: '95%', maxWidth: 1100, maxHeight: '90vh', overflow: 'auto' }}>
            <ModalClose />
            {detailBody}
          </ModalDialog>
        </Modal>
      )}

        {/* Status modal */}
        <Modal open={statusModal} onClose={() => setStatusModal(false)}>
          <ModalDialog><ModalClose /><Typography level="h4">Update Status</Typography>
            <form onSubmit={handleStatusSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Status</FormLabel>
                <Select value={statusForm.status} onChange={(_, v) => setStatusForm((p) => ({ ...p, status: v }))}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Date</FormLabel>
                <Input type="date" value={statusForm.changeDate} onChange={(e) => setStatusForm((p) => ({ ...p, changeDate: e.target.value }))} required />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Remarks</FormLabel>
                <Textarea value={statusForm.remarks} onChange={(e) => setStatusForm((p) => ({ ...p, remarks: e.target.value }))} minRows={2} />
              </FormControl>
              <Button type="submit" loading={statusSubmitting}>Save</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* Edit modal */}
        <Modal open={editOpen} onClose={() => setEditOpen(false)}>
          <ModalDialog sx={{ maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}><ModalClose /><Typography level="h4">Edit Equipment</Typography>
            <form onSubmit={handleEditSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Name</FormLabel><Input value={editForm.equipment_name} onChange={(e) => setEditForm((p) => ({ ...p, equipment_name: e.target.value }))} required /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Purchase Date</FormLabel><Input type="date" value={editForm.purchase_date} onChange={(e) => setEditForm((p) => ({ ...p, purchase_date: e.target.value }))} required /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Cost</FormLabel><Input type="number" value={editForm.purchase_cost} onChange={(e) => setEditForm((p) => ({ ...p, purchase_cost: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Warranty Until</FormLabel><Input type="date" value={editForm.warranty_until} onChange={(e) => setEditForm((p) => ({ ...p, warranty_until: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Serial / Brand</FormLabel>
                <Input placeholder="Serial" value={editForm.serial_no} onChange={(e) => setEditForm((p) => ({ ...p, serial_no: e.target.value }))} sx={{ mb: 0.5 }} />
                <Input placeholder="Brand/Make" value={editForm.brand_make} onChange={(e) => setEditForm((p) => ({ ...p, brand_make: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Vendor</FormLabel>
                <Select value={editForm.vendor_id} onChange={(_, v) => setEditForm((p) => ({ ...p, vendor_id: v || '' }))}>
                  <Option value="">None</Option>
                  {vendors.map((v) => <Option key={v.id} value={String(v.id)}>{v.name}</Option>)}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Location</FormLabel><Input value={editForm.location} onChange={(e) => setEditForm((p) => ({ ...p, location: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Notes</FormLabel><Textarea value={editForm.notes} onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))} minRows={2} /></FormControl>
              <Button type="submit" loading={editSubmitting}>Save</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* AMC modal */}
        <Modal open={!!amcModal} onClose={() => setAmcModal(null)}>
          <ModalDialog><ModalClose /><Typography level="h4">{amcModal?.mode === 'create' ? 'Add AMC' : 'Edit AMC'}</Typography>
            <form onSubmit={handleAmcSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Provider</FormLabel><Input value={amcForm.provider_name} onChange={(e) => setAmcForm((p) => ({ ...p, provider_name: e.target.value }))} required /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Contact</FormLabel><Input value={amcForm.provider_contact} onChange={(e) => setAmcForm((p) => ({ ...p, provider_contact: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}>
                <FormLabel>Start date</FormLabel>
                <Input
                  type="date"
                  value={amcForm.start_date}
                  onChange={(e) => setAmcForm((p) => ({ ...p, start_date: e.target.value }))}
                  required
                />
              </FormControl>
              <ExpiryDateShortcuts
                baseDate={amcForm.start_date}
                shortcuts={YEAR_END_SHORTCUTS}
                onExpiryChange={(end_date) => setAmcForm((p) => ({ ...p, end_date }))}
              />
              <FormControl sx={{ mb: 1 }}>
                <FormLabel>End date</FormLabel>
                <Input
                  type="date"
                  value={amcForm.end_date}
                  onChange={(e) => setAmcForm((p) => ({ ...p, end_date: e.target.value }))}
                  required
                />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Cost</FormLabel><Input type="number" value={amcForm.contract_cost} onChange={(e) => setAmcForm((p) => ({ ...p, contract_cost: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Status</FormLabel>
                <Select value={amcForm.status} onChange={(_, v) => setAmcForm((p) => ({ ...p, status: v }))}>
                  {Object.entries(AMC_STATUS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Notes</FormLabel><Textarea value={amcForm.notes} onChange={(e) => setAmcForm((p) => ({ ...p, notes: e.target.value }))} minRows={2} /></FormControl>
              <Button type="submit">Save</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* AMC history modal */}
        <Modal open={!!amcHistoryModal} onClose={() => setAmcHistoryModal(null)}>
          <ModalDialog sx={{ maxWidth: 600 }}><ModalClose /><Typography level="h4">AMC History</Typography>
            <Table size="sm" sx={{ mb: 2 }}>
              <thead><tr><th>Date</th><th>Event</th><th>Details</th></tr></thead>
              <tbody>
                {amcHistory.map((h) => (
                  <tr key={h.id}>
                    <td>{formatDisplayDate(h.event_date)}</td>
                    <td>{h.event_type}</td>
                    <td>{h.details || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <form onSubmit={handleAmcEventSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Event</FormLabel>
                <Select value={amcEventForm.event_type} onChange={(_, v) => setAmcEventForm((p) => ({ ...p, event_type: v }))}>
                  <Option value="visit">Visit</Option><Option value="renewed">Renewed</Option><Option value="note">Note</Option>
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Date</FormLabel><Input type="date" value={amcEventForm.event_date} onChange={(e) => setAmcEventForm((p) => ({ ...p, event_date: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Details</FormLabel><Textarea value={amcEventForm.details} onChange={(e) => setAmcEventForm((p) => ({ ...p, details: e.target.value }))} /></FormControl>
              <Button type="submit">Add Event</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* Service modal */}
        <Modal open={!!svcModal} onClose={() => setSvcModal(null)}>
          <ModalDialog sx={{ maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}><ModalClose /><Typography level="h4">{svcModal?.mode === 'create' ? 'Add Service' : 'Edit Service'}</Typography>
            <form onSubmit={handleSvcSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Problem</FormLabel><Textarea value={svcForm.problem_reported} onChange={(e) => setSvcForm((p) => ({ ...p, problem_reported: e.target.value }))} required minRows={2} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Service Provider</FormLabel><Input value={svcForm.service_provider} onChange={(e) => setSvcForm((p) => ({ ...p, service_provider: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Technician</FormLabel>
                <Input placeholder="Name" value={svcForm.technician_name} onChange={(e) => setSvcForm((p) => ({ ...p, technician_name: e.target.value }))} sx={{ mb: 0.5 }} />
                <Input placeholder="Contact" value={svcForm.technician_contact} onChange={(e) => setSvcForm((p) => ({ ...p, technician_contact: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Reported / Out / In dates</FormLabel>
                <Input type="date" value={svcForm.reported_date} onChange={(e) => setSvcForm((p) => ({ ...p, reported_date: e.target.value }))} sx={{ mb: 0.5 }} />
                <Input type="date" placeholder="Out" value={svcForm.out_date} onChange={(e) => setSvcForm((p) => ({ ...p, out_date: e.target.value }))} sx={{ mb: 0.5 }} />
                <Input type="date" placeholder="In" value={svcForm.in_date} onChange={(e) => setSvcForm((p) => ({ ...p, in_date: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Charge / Parts</FormLabel>
                <Input type="number" value={svcForm.service_charge} onChange={(e) => setSvcForm((p) => ({ ...p, service_charge: e.target.value }))} sx={{ mb: 0.5 }} />
                <Input placeholder="Parts replaced" value={svcForm.parts_replaced} onChange={(e) => setSvcForm((p) => ({ ...p, parts_replaced: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Status</FormLabel>
                <Select value={svcForm.status} onChange={(_, v) => setSvcForm((p) => ({ ...p, status: v }))}>
                  {Object.entries(SVC_STATUS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Remarks</FormLabel><Textarea value={svcForm.remarks} onChange={(e) => setSvcForm((p) => ({ ...p, remarks: e.target.value }))} minRows={2} /></FormControl>
              <Button type="submit">Save</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* Service history modal */}
        <Modal open={!!svcHistoryModal} onClose={() => setSvcHistoryModal(null)}>
          <ModalDialog sx={{ maxWidth: 600 }}><ModalClose /><Typography level="h4">Service History</Typography>
            <Table size="sm" sx={{ mb: 2 }}>
              <thead><tr><th>Date</th><th>Event</th><th>Details</th></tr></thead>
              <tbody>
                {svcHistory.map((h) => (
                  <tr key={h.id}>
                    <td>{formatDisplayDate(h.event_date)}</td>
                    <td>{h.event_type}</td>
                    <td>{h.details || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <form onSubmit={handleSvcEventSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Event</FormLabel>
                <Select value={svcEventForm.event_type} onChange={(_, v) => setSvcEventForm((p) => ({ ...p, event_type: v }))}>
                  <Option value="note">Note</Option><Option value="parts_replaced">Parts Replaced</Option><Option value="assigned">Assigned</Option>
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Date</FormLabel><Input type="date" value={svcEventForm.event_date} onChange={(e) => setSvcEventForm((p) => ({ ...p, event_date: e.target.value }))} /></FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Details</FormLabel><Textarea value={svcEventForm.details} onChange={(e) => setSvcEventForm((p) => ({ ...p, details: e.target.value }))} /></FormControl>
              <Button type="submit">Add Event</Button>
            </form>
          </ModalDialog>
        </Modal>
    </>
  );
};

export default EquipmentPopup;
